import { Trash2 } from 'lucide-react'
import clsx from 'clsx'
import { InfoTooltip } from '@/components/InfoTooltip'
import { fileKeyLabel } from '@/lib/fileKeys'
import type { DiffChange, DiffFileEntry, ValidationError } from '@/types/diff'
import { DiffChangeRow } from './DiffChangeRow'

export interface DiffFileBlockProps {
  fileEntry: DiffFileEntry
  /** All validate-draft errors for this file_key (not just ones matching a
   * rendered row) - PRD 07 AC3 requires every failing {file_key, path} to
   * have a visible note on its diff block, so any error that doesn't match
   * a specific change row still needs to surface here, not only in the
   * summary banner. */
  fileErrors: ValidationError[]
  highlightedAnchor: string | null
  /** Discards this file's entire draft edit - the "whole group" level.
   * Undefined in the read-only historical compare mode (no draft to
   * discard from). */
  onDiscardFile?: () => void
  /** Reverts one field's change back to its live value, leaving the rest of
   * this file's draft edit intact - the "per field" level. Undefined for a
   * whole-file added/removed block, which has no individual fields to
   * revert (only discarding the whole file applies there). */
  onRevertField?: (change: DiffChange) => void
  /** Path of the field currently being reverted, if any, for its own
   * loading state. */
  revertingPath?: string | null
}

// A clients.json auth.api_key leaf is masked (never a real value) in this
// screen's diff data (PRD 23 step 3). Reverting it would PUT the literal
// "********" string back, which PRD 21's own unmask-on-write logic reads as
// "leave the current draft value unchanged" - not a real revert to the live
// key. Rather than silently no-op, per-field revert is disabled for these
// rows; discarding the whole file (which never touches content at all) is
// the safe way to undo a key rotation.
const MASKED_VALUE = '********'
function isRevertable(change: { oldValue: unknown; newValue: unknown }): boolean {
  return change.oldValue !== MASKED_VALUE && change.newValue !== MASKED_VALUE
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  added: 'bg-success/20 text-success',
  removed: 'bg-danger/20 text-danger',
  modified: 'bg-warning/20 text-warning',
}

const STATUS_DESCRIPTIONS: Record<string, string> = {
  added: 'This whole file is new in this draft.',
  removed: 'This whole file will be deleted when published.',
  modified: 'One or more fields below have changed.',
}

// One block per file with a real change or a validation error. "added"/
// "removed" render as a single distinct card (PRD 07 AC5) rather than
// exploding the backend's synthetic whole-file entry (whose value is the
// entire file content) into per-leaf rows, which would violate AC1's
// "never raw JSON" bar.
export function DiffFileBlock({
  fileEntry,
  fileErrors,
  highlightedAnchor,
  onDiscardFile,
  onRevertField,
  revertingPath,
}: DiffFileBlockProps) {
  const blockId = `${fileEntry.fileKey}-block`
  const isWholeFile = fileEntry.status === 'added' || fileEntry.status === 'removed'
  const matchedPaths = new Set(fileEntry.changes.map((change) => change.path))
  const unmatchedErrors = fileErrors.filter((error) => !matchedPaths.has(error.path))

  return (
    <section
      id={blockId}
      className={clsx(
        'flex flex-col gap-3 rounded-control border bg-surface p-4 transition-colors',
        highlightedAnchor === blockId ? 'border-accent' : 'border-border',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-header font-semibold text-text-primary">{fileKeyLabel(fileEntry.fileKey)}</h2>
          <span
            className={clsx(
              'rounded-pill px-2 py-0.5 text-meta font-medium capitalize',
              STATUS_BADGE_CLASSES[fileEntry.status],
            )}
          >
            {fileEntry.status}
          </span>
          <InfoTooltip text={STATUS_DESCRIPTIONS[fileEntry.status]} />
        </div>
        {onDiscardFile && (
          <button
            type="button"
            onClick={onDiscardFile}
            className="flex items-center gap-1 text-meta font-medium text-text-secondary hover:text-danger"
          >
            <Trash2 size={14} strokeWidth={1.5} />
            Discard {fileKeyLabel(fileEntry.fileKey)}
          </button>
        )}
      </div>

      {isWholeFile ? (
        <p className="text-body text-text-secondary">
          {fileEntry.status === 'added'
            ? 'This entire configuration file was added.'
            : 'This entire configuration file was removed.'}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {fileEntry.changes.map((change) => (
            <DiffChangeRow
              key={change.path}
              anchorId={`${fileEntry.fileKey}-${change.path}`}
              change={change}
              errorMessage={fileErrors.find((error) => error.path === change.path)?.message}
              highlighted={highlightedAnchor === `${fileEntry.fileKey}-${change.path}`}
              onRevert={onRevertField && isRevertable(change) ? () => onRevertField(change) : undefined}
              maskedNoRevert={Boolean(onRevertField) && !isRevertable(change)}
              reverting={revertingPath === change.path}
            />
          ))}
        </div>
      )}

      {unmatchedErrors.length > 0 && (
        <div className="flex flex-col gap-1 rounded-control border border-danger/40 bg-danger/10 px-3 py-2">
          {unmatchedErrors.map((error) => (
            <span key={error.path} className="text-meta text-danger">
              {error.message}
            </span>
          ))}
        </div>
      )}
    </section>
  )
}
